import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, timer, Subject, throwError } from 'rxjs';
import { map, tap, switchMap, takeUntil, catchError } from 'rxjs/operators';
import { AppContext } from '../Shared/common';
import { IOrganisation } from '../Models/Common/Organisation';
import { User } from '../Models/FitnessBox/User';
import { OrgService } from './org.service';
import { LoginRequest, LoginResponse, TokenResponse } from '../Models/Common/AuthModels';
import { GetUserByUsernameResponse, RegisterUserRequest, RegisterUserResponse } from '../Models/Common/AuthModels';
import { ForgotPasswordResponse, ChangePasswordRequest, ChangePasswordResponse } from '../Models/Common/ForgotPasswordModels';
import { Global } from '../Shared/global';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl = Global.BASE_USER_ENDPOINT() + 'auth';
  private currentUserSubject = new BehaviorSubject<User | null>(this.getUserFromStorage());
  private currentOrgSubject = new BehaviorSubject<IOrganisation | null>(this.getOrgFromStorage());
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);

  public currentUser$ = this.currentUserSubject.asObservable();
  public currentOrg$ = this.currentOrgSubject.asObservable();
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  public authReady$ = new BehaviorSubject<boolean>(false);

  // Token refresh configuration
  private readonly ACCESS_TOKEN_REFRESH_THRESHOLD = 0.80; // Refresh when 80% of lifetime passed
  private readonly BUFFER_TIME_MS = 5000; // 5 second buffer before actual expiry
  private refreshTokenTimer: any;
  private destroy$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private appContext: AppContext,
    private orgService: OrgService
  ) {
    // Initialize token refresh on service creation
    const isLoggedIn = this.isLoggedIn();
    this.isAuthenticatedSubject.next(isLoggedIn);
    this.authReady$.next(true);

    if (isLoggedIn) {
      this.setupTokenRefreshTimer();
    }
  }

  /**
   * Login user with credentials
   * Receives access token, refresh token, user, and organizations in one response
   */
  login(credentials: LoginRequest): Observable<LoginResponse> {
    const orgSlug = this.orgService.getStoredOrgSlug();
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'uname': credentials.username,
      'password': credentials.password,
      'orgName': orgSlug || ''
    });

    //const loginPayload = {
    //  ...credentials,
    //  orgSlug: orgSlug
    //};

    return this.http.get<any>(`${this.apiUrl}/getauthtoken`, { headers }).pipe(
      tap(response => {
        if (response.success && response.token) {
          // Store tokens
          this.setAuthToken(response.token);
          this.setRefreshToken(response.refreshToken);

          // Store user and organization
          if (response.user) {
            this.setCurrentUser(response.user);
            //Update Selected Org Unit
            // Ensure current org is set from storage
            if (this.currentOrgSubject) { //&& this.currentOrgSubject.value
              var Org = this.getCurrentOrganization();
              for (let i = 0; i < Org.OrgUnits.length; i++) {
                Org.OrgUnits[i].IsSelected = false;
                if (Org.OrgUnits[i].UnitId == response.user?.UnitId) {
                  Org.OrgUnits[i].IsSelected = true;
                }
              }

              if (Org.OrgUnits != null && Org.OrgUnits.length > 0 && !Org.OrgUnits.some((u: any) => u.IsSelected)) {
                Org.OrgUnits[0].IsSelected = true;
              }

              this.selectOrganization(Org);
              response.organization = Org;
            }
          }

          if (response.organization) {
            //this.setOrganizations([response.organization]);
          }

          this.isAuthenticatedSubject.next(true);

          // Setup automatic token refresh
          this.setupTokenRefreshTimer();
        }
      }),
      // Map to LoginResponse format for backward compatibility
      map(response => ({
        success: response.success,
        message: response.message,
        user: response.user,
        organization: response.organization,
        orgSlug: response.orgSlug
      }))
    );
  }

  /**
   * Refresh access token using refresh token
   * Called automatically before token expiration
   */
  refreshAccessToken(): Observable<TokenResponse> {
    const refreshToken = this.getRefreshToken();
    const currentUserId: number = this.getCurrentUser()?.ID ?? 0;
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    debugger
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${refreshToken}`,
      'Content-Type': 'application/json',
      'UserId': currentUserId.toString()
    });

    return this.http.post<TokenResponse>(`${this.apiUrl}/refresh-token`, {}, { headers }).pipe(
      tap(response => {
        if (response && response.Token) {
          // Store new access token
          this.setAuthToken(response.Token);

          // Keep refresh token (unless server returns a new one)
          if (response.RefreshToken) {
            this.setRefreshToken(response.RefreshToken);
          }

          // Reset the refresh timer with new token expiration
          this.setupTokenRefreshTimer();
        }
      })
    );
  }

  /**
   * Logout user and revoke refresh token
   */
  logout(): Observable<any> {
    const refreshToken = this.getRefreshToken();
    const headers = refreshToken
      ? new HttpHeaders({ 'Authorization': `Bearer ${refreshToken}` })
      : undefined;

    return this.http.post(`${this.apiUrl}/revoke-token`, {}, { headers }).pipe(
      tap(() => {
        this.clearAuth();
        this.stopTokenRefreshTimer();
      }),
      catchError(error => {
        debugger;
        // Log the error for debugging
        Global.LogError('Token revocation failed:', error);

        // Determine error type and message
        let errorMessage = 'Logout failed. Please try again.';
        let errorStatus = error?.status;

        if (error instanceof HttpErrorResponse) {
          if (errorStatus === 401 || errorStatus === 403) {
            errorMessage = 'Your session has expired. Please log in again.';
          } else if (errorStatus === 404) {
            errorMessage = 'Revoke endpoint not found. Your session has been cleared locally.';
          } else if (errorStatus === 500 || errorStatus === 503) {
            errorMessage = 'Server error during logout. Your session has been cleared locally.';
          } else if (errorStatus === 0 || !errorStatus) {
            errorMessage = 'Network error during logout. Your session has been cleared locally.';
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          }
        }

        Global.LogWarning(`Logout error (${errorStatus}): ${errorMessage}`);

        // Always clear auth state and stop timers, even if revoke fails
        // This ensures the user is logged out locally
        this.clearAuth();
        this.stopTokenRefreshTimer();

        // Return the error so components can handle it if needed
        // But don't prevent logout completion
        return throwError(() => ({
          success: false,
          message: errorMessage,
          status: errorStatus,
          originalError: error
        }));
      })
    );
  }

  /**
   * Select organization
   */
  selectOrganization(org: IOrganisation): void {
    this.currentOrgSubject.next(org);
    localStorage.setItem('currentOrg', JSON.stringify(org));
    this.appContext.CurrentOrg = org;
  }

  /**
   * Get current user
   */
  getCurrentUser(): User | null {
    const currentUser = this.currentUserSubject.value;

    // If currentUserSubject has a value, return it
    if (currentUser) {
      return currentUser;
    }

    // Fallback to localStorage if BehaviorSubject is null or undefined
    const userFromStorage = this.getUserFromStorage();
    if (userFromStorage) {
      // Update the BehaviorSubject for consistency
      this.currentUserSubject.next(userFromStorage);
    }

    return userFromStorage;
  }

  /**
   * Get current organization
   */
  getCurrentOrganization(): IOrganisation | null {
    const currentOrg = this.appContext.CurrentOrg;
    const orgFromStorage = this.getOrgFromStorage();
    const selectedUnitCount = currentOrg?.OrgUnits?.filter(u => u.IsSelected).length || 0;
    const selectedStoredUnitCount = orgFromStorage?.OrgUnits?.filter(u => u.IsSelected).length || 0;

    // If currentOrgSubject has a value, return it
    if (currentOrg && currentOrg.OrgId && currentOrg.OrgId > 0 && selectedUnitCount == 1) {
      return currentOrg;
    }

    // Fallback to localStorage if BehaviorSubject is null or undefined
    
    if (orgFromStorage) {
      // Update the BehaviorSubject for consistency
      this.currentOrgSubject.next(orgFromStorage);
      this.appContext.CurrentOrg = orgFromStorage;
    }

    return orgFromStorage;
  }

  /**
   * Check if user is logged in
   */
  isLoggedIn(): boolean {
    const user = localStorage.getItem('currentUser');
    const token = localStorage.getItem('authToken');
    const orgSlug = localStorage.getItem('orgSlug');

    if (!token) return false;

    // Additional check: verify token is not already expired
    const expiryTime = localStorage.getItem('tokenExpiry');
    if (expiryTime) {
      const now = Date.now();
      if (parseInt(expiryTime, 10) < now) {
        this.clearAuth();
        return false;
      }
    }

    return !!(user && token && orgSlug);
  }

  /**
   * Get auth token
   */
  getAuthToken(): string | null {
    return localStorage.getItem('authToken');
  }

  /**
   * Get refresh token
   */
  getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  /**
   * Setup automatic token refresh
   * Refreshes token before expiration based on threshold
   */
  private setupTokenRefreshTimer(): void {
    // Clear existing timer
    this.stopTokenRefreshTimer();

    const expiryTime = localStorage.getItem('tokenExpiry');
    if (!expiryTime) return;

    const expiryMs = parseInt(expiryTime, 10);
    const now = Date.now();
    const timeUntilExpiry = expiryMs - now;

    if (timeUntilExpiry <= 0) {
      // Token already expired, try to refresh immediately
      this.refreshAccessToken().subscribe(
        () => this.setupTokenRefreshTimer(), // Set new timer with refreshed token
        (error) => {
          Global.LogError('Token refresh failed:', error);
          this.clearAuth();
        }
      );
      return;
    }

    // Calculate when to refresh: when 80% of lifetime has passed
    const refreshTime = timeUntilExpiry * this.ACCESS_TOKEN_REFRESH_THRESHOLD;

    // Add buffer to ensure refresh happens before actual expiry
    const delayMs = Math.max(0, refreshTime - this.BUFFER_TIME_MS);

    Global.Log(`Token will refresh in ${Math.round(delayMs / 1000)} seconds`);

    // Setup timer for automatic refresh
    this.refreshTokenTimer = setTimeout(() => {
      Global.Log('Auto-refreshing token...');
      this.refreshAccessToken().subscribe(
        () => {
          Global.Log('Token refreshed successfully');
          this.setupTokenRefreshTimer(); // Setup next refresh
        },
        (error) => {
          Global.LogError('Automatic token refresh failed:', error);
          this.clearAuth();
        }
      );
    }, delayMs);
  }

  /**
   * Stop automatic token refresh timer
   */
  public stopTokenRefreshTimer(): void {
    if (this.refreshTokenTimer) {
      clearTimeout(this.refreshTokenTimer);
      this.refreshTokenTimer = null;
    }
  }

  /**
   * Set auth token and expiry time
   */
  private setAuthToken(token: string): void {
    localStorage.setItem('authToken', token);

    // Decode token to get expiry time
    const expiryTime = this.getTokenExpiryTime(token);
    if (expiryTime) {
      localStorage.setItem('tokenExpiry', expiryTime.toString());
    }
  }

  /**
   * Set refresh token
   */
  private setRefreshToken(token: string): void {
    localStorage.setItem('refreshToken', token);
  }

  /**
   * Decode JWT token to get expiry time
   */
  private getTokenExpiryTime(token: string): number | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      const decoded = JSON.parse(atob(parts[1]));
      return decoded.exp ? decoded.exp * 1000 : null; // Convert to milliseconds
    } catch (error) {
      Global.LogError('Error decoding token:', error);
      return null;
    }
  }

  /**
   * Set current user
   */
  private setCurrentUser(user: User): void {
    this.currentUserSubject.next(user);
    localStorage.setItem('currentUser', JSON.stringify(user));
    this.appContext.CurrentUser = user;
  }

  /**
   * Set organizations
   */
  private setOrganizations(organizations: IOrganisation[]): void {
    localStorage.setItem('organizations', JSON.stringify(organizations));
    if (organizations.length > 0) {
      this.selectOrganization(organizations[0]);
    }
  }

  /**
   * Get user from storage
   */
  private getUserFromStorage(): User | null {
    const user = localStorage.getItem('currentUser');
    return user ? JSON.parse(user) : null;
  }

  /**
   * Get organization from storage
   */
  private getOrgFromStorage(): IOrganisation | null {
    const org = localStorage.getItem('currentOrg');
    return org ? JSON.parse(org) : null;
  }

  /**
   * Clear authentication and stop refresh timer
   */
  public clearAuth(): void {
    this.currentUserSubject.next(null);
    this.currentOrgSubject.next(null);
    if (this.isAuthenticatedSubject)
      this.isAuthenticatedSubject.next(false);

    this.stopTokenRefreshTimer();

    localStorage.removeItem('currentUser');
    //localStorage.removeItem('currentOrg');
    localStorage.removeItem('organizations');
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenExpiry');
    //localStorage.removeItem('orgSlug');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopTokenRefreshTimer();
  }

  /**
   * Get user by username for registration lookup
   */
  getUserByUserName(username: string): Observable<GetUserByUsernameResponse> {
    return this.http.get<GetUserByUsernameResponse>(
      `${this.apiUrl}/GetUserByUserName?username=${encodeURIComponent(username)}`
    ).pipe(
      catchError(error => {
        Global.LogError('Get user by username failed:', error);
        return throwError(() => ({
          success: false,
          message: error.error?.message || 'User not found'
        }));
      })
    );
  }

  /**
   * Register new user
   */
  registerUser(user: RegisterUserRequest): Observable<RegisterUserResponse> {
    return this.http.post<RegisterUserResponse>(
      `${this.apiUrl}/RegisterUser`,
      user
    ).pipe(
      catchError(error => {
        Global.LogError('User registration failed:', error);
        
        let errorMessage = 'Registration failed. Please try again.';
        const errorData = error.error;

        if (Array.isArray(errorData)) {
          // Backend returns array of error messages
          errorMessage = errorData.join(', ');
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        }

        return throwError(() => ({
          success: false,
          message: errorMessage,
          errors: Array.isArray(errorData) ? errorData : [errorMessage]
        }));
      })
    );
  }

  /**
   * Request forgot password verification code
   */
  requestForgotPasswordCode(username: string): Observable<ForgotPasswordResponse> {
    return this.http.get<ForgotPasswordResponse>(
      `${this.apiUrl}/RequestForgotPasswordCode?username=${encodeURIComponent(username)}&requestType=resetpassword`
    ).pipe(
      catchError(error => {
        Global.LogError('Request forgot password code failed:', error);

        let errorMessage = 'Failed to request verification code. Please try again.';
        const errorData = error.error;

        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (Array.isArray(errorData)) {
          errorMessage = errorData.join(', ');
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        }

        return throwError(() => ({
          success: false,
          message: errorMessage,
          errors: Array.isArray(errorData) ? errorData : [errorMessage]
        }));
      })
    );
  }

  /**
   * Change password using verification code
   */
  changePassword(request: ChangePasswordRequest): Observable<ChangePasswordResponse> {
    return this.http.post<ChangePasswordResponse>(
      `${this.apiUrl}/ChangePassword?username=${encodeURIComponent(request.UserName)}`,
      {
        UserName: request.UserName,
        ValidationCode: request.ValidationCode,
        Password: request.Password,
        RequestId: request.RequestId,
        ExpiresAt: request.ExpiresAt
      }
    ).pipe(
      tap(response => {
        Global.Log('Password change response received:', response);
      }),
      catchError(error => {
        Global.LogError('Change password failed:', error);

        let errorMessage = 'Password reset failed. Please try again.';
        const errorData = error.error;

        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (Array.isArray(errorData)) {
          errorMessage = errorData.join(', ');
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        }

        return throwError(() => ({
          success: false,
          message: errorMessage,
          errors: Array.isArray(errorData) ? errorData : [errorMessage]
        }));
      })
    );
  }

  /**
   * Request forgot password verification code
   */
  requestForgotPasswordCodeByRequestor(username: string,requestorEmail: string): Observable<ForgotPasswordResponse> {
    return this.http.get<ForgotPasswordResponse>(
      `${this.apiUrl}/RequestForgotPasswordCode?username=${encodeURIComponent(username)}&requestType=resetpassword&requestorEmail=${encodeURIComponent(requestorEmail)}`
    ).pipe(
      catchError(error => {
        Global.LogError('Request forgot password code failed:', error);

        let errorMessage = 'Failed to request verification code. Please try again.';
        const errorData = error.error;

        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (Array.isArray(errorData)) {
          errorMessage = errorData.join(', ');
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        }

        return throwError(() => ({
          success: false,
          message: errorMessage,
          errors: Array.isArray(errorData) ? errorData : [errorMessage]
        }));
      })
    );
  }
}
